/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ResourceExecutionErrorCodeCount } from "~/generated/graphql";
import { isNotNil } from "~/tools";
import type { SvgName } from "~/tools/types";
import { getLogActionDescriptionFromCounts } from "~/utils/logErrorCode";
import { getLatestAttemptSummaryId } from "../helpers";
import { EventEntity } from "../types";

export type EventActionDescription = {
  text: string;
  icon?: SvgName;
  iconColor?: string;
};

type EventSourceLike = {
  actions?: EventEntity[] | null;
  actionBatches?: EventEntity[] | null;
};
type ActionWithErrorCodeCounts = {
  id: string;
  errorCodeCounts?: ResourceExecutionErrorCodeCount[] | null;
};
type EventSourceWithErrorCodeCounts = {
  actions?: ActionWithErrorCodeCounts[] | null;
  actionBatches?: { actions?: ActionWithErrorCodeCounts[] | null }[] | null;
};

export function buildErrorCodeCountsByActionId(
  event: EventSourceWithErrorCodeCounts | null | undefined
): Record<string, ResourceExecutionErrorCodeCount[]> {
  const topLevel = (event?.actions ?? []).filter(isNotNil);
  const batched = (event?.actionBatches ?? [])
    .filter(isNotNil)
    .flatMap((batch) => (batch.actions ?? []).filter(isNotNil));

  return [...topLevel, ...batched].reduce<
    Record<string, ResourceExecutionErrorCodeCount[]>
  >(
    (acc, action) => ({ ...acc, [action.id]: action.errorCodeCounts ?? [] }),
    {}
  );
}

export function getFlatActionEntities(
  eventSource: EventSourceLike
): EventEntity[] {
  return [
    ...(eventSource.actions ?? []).filter(isNotNil),
    ...(eventSource.actionBatches ?? [])
      .filter(isNotNil)
      .flatMap((batch) => batch.actions),
  ].filter(isNotNil);
}

export function getExpandedEventEntities(
  eventSource: EventSourceLike
): EventEntity[] {
  return [...(eventSource.actionBatches ?? []), ...(eventSource.actions ?? [])]
    .filter(isNotNil)
    .map((entity) => ({
      ...entity,
      subRows: "actions" in entity ? entity.actions : [],
    }));
}

export function getActionErrorCodeCounts(
  errorCodeCountsByActionId: Record<string, ResourceExecutionErrorCodeCount[]>,
  eventEntity: Pick<EventEntity, "id" | "attempts">
) {
  return (
    errorCodeCountsByActionId[getLatestAttemptSummaryId(eventEntity)] ?? []
  );
}

export function getLogActionDescription(
  errorCodeCounts: ResourceExecutionErrorCodeCount[] = []
): EventActionDescription | undefined {
  return getLogActionDescriptionFromCounts(errorCodeCounts);
}

export function getActionDescriptionForEntity({
  errorCodeCountsByActionId,
  eventEntity,
  isEventFinished,
}: {
  errorCodeCountsByActionId: Record<string, ResourceExecutionErrorCodeCount[]>;
  eventEntity: EventEntity;
  isEventFinished: boolean;
}): EventActionDescription | undefined {
  if (!isEventFinished) {
    return undefined;
  }

  return getLogActionDescription(
    getActionErrorCodeCounts(errorCodeCountsByActionId, eventEntity)
  );
}
